import type { ComputedRef, Ref } from 'vue'
import { inject } from 'vue'
import type { AxiosInstance } from 'axios'
import type { PitcherInfo } from '@lib/types/app'
import type {
  BulkDeleteRequest,
  BulkDeleteResponse,
  BulkUpdateTagsRequest,
  BulkUpdateTags,
  BulkUpdateMetadataRequest,
  BulkUpdateMetadata,
  MiniFile,
} from '@openapi'
import { BulkUpdateTagsOperationEnum, BulkUpdateMetadataOperationEnum } from '@openapi'

export default function useBulkActions(emit?: (event: 'filesDeleted', ids: MiniFile['id'][]) => void) {
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>('pitcherInfo')
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')

  async function performPostRequest<T>(endpointSuffix: string, payload: unknown): Promise<T> {
    if (!pitcherInfo?.value) throw new Error('Pitcher info is not provided')
    if (!restApiAxios?.value) throw new Error('HTTP client is not initialized')

    return await restApiAxios.value.post<unknown, T>(endpointSuffix, payload)
  }

  const performTagUpdate = (
    operation: BulkUpdateTagsOperationEnum,
    payload: Omit<BulkUpdateTagsRequest, 'instance_id' | 'operation'>,
  ): Promise<BulkUpdateTags> => {
    const tagPayload: BulkUpdateTagsRequest = {
      ...payload,
      instance_id: pitcherInfo!.value!.instanceId,
      operation,
    }
    return performPostRequest<BulkUpdateTags>('bulk/tags/', tagPayload)
  }

  const performMetadataUpdate = (
    operation: BulkUpdateMetadataOperationEnum,
    payload: Omit<BulkUpdateMetadataRequest, 'instance_id' | 'operation'>,
  ): Promise<BulkUpdateMetadata> => {
    const metadataPayload: BulkUpdateMetadataRequest = {
      ...payload,
      instance_id: pitcherInfo!.value!.instanceId,
      operation,
    }
    return performPostRequest<BulkUpdateMetadata>('bulk/metadata/', metadataPayload)
  }

  const addTags = (payload: Omit<BulkUpdateTagsRequest, 'instance_id' | 'operation'>) =>
    performTagUpdate(BulkUpdateTagsOperationEnum.ADD, payload)
  const removeTags = (payload: Omit<BulkUpdateTagsRequest, 'instance_id' | 'operation'>) =>
    performTagUpdate(BulkUpdateTagsOperationEnum.REMOVE, payload)

  const mergeMetadata = (payload: Omit<BulkUpdateMetadataRequest, 'instance_id' | 'operation'>) =>
    performMetadataUpdate(BulkUpdateMetadataOperationEnum.UPDATE, payload)
  const replaceMetadata = (payload: Omit<BulkUpdateMetadataRequest, 'instance_id' | 'operation'>) =>
    performMetadataUpdate(BulkUpdateMetadataOperationEnum.REPLACE, payload)

  const deleteCanvases = (payload: BulkDeleteRequest) =>
    performPostRequest<BulkDeleteResponse>('canvases/bulk-delete/', payload)
  const deleteFiles = async (payload: BulkDeleteRequest) => {
    emit?.('filesDeleted', payload.ids)
    const response = await performPostRequest<BulkDeleteResponse>('files/bulk-delete/', payload)
    return response
  }
  const deleteFolders = (payload: BulkDeleteRequest) =>
    performPostRequest<BulkDeleteResponse>('folders/bulk-delete/', payload)
  const deleteFavorites = (payload: BulkDeleteRequest) =>
    performPostRequest<BulkDeleteResponse>('favorites/bulk-delete/', payload)

  return {
    deleteCanvases,
    deleteFiles,
    deleteFolders,
    deleteFavorites,
    addTags,
    removeTags,
    mergeMetadata,
    replaceMetadata,
  }
}
